/**
 * AI Mockup Card - Fixed Version with Dual Angle Support
 */
function aiMockupCard() {
  return {
    // --- States ---
    loading: false,
    generating: false,
    success: false,
    imageLoaded: false,
    showShimmer: false,
    showContactForm: false,
    contactFormSuccess: false,
    linkCopied: false,
    touchStartX: 0,
    touchEndX: 0,

    // --- Data ---
    formData: {
      customerName: '',
      customerEmail: '',
      jewelrySKU: '',
      stoneSKU: '',
      metalColor: 'original'
    },
    mockupResult: null, // Will hold { imageUrl, imageUrl2, galleryId, ... }
    activeImageIndex: 1, // 1 for main angle, 2 for second angle
    feedback: { rating: null, submitted: false },
    _backgroundResult: null,
    _backgroundPromise: null,

    // --- Loading Messages ---
    loadingMessages: [
      'Polishing the final pixels...', 'Setting the diamond in place...', 'Adding sparkle to your design...',
      'Finding the perfect angle...', 'Crafting your masterpiece...'
    ],
    currentMessageIndex: 0,
    displayedLoadingMessage: '',
    loadingMessageTimeout: null,
    loadingMessageAnimationId: 0,

    // --- Init ---
    init() {
      this.loadDataFromConfirmation();
    },

    // --- Image Display Logic ---
    getActiveImageUrl() {
      if (!this.mockupResult) return '';
      return this.activeImageIndex === 1 ? this.mockupResult.imageUrl : (this.mockupResult.imageUrl2 || this.mockupResult.imageUrl);
    },

    setActiveImage(index) {
      if (index === 1 && this.mockupResult?.imageUrl) {
        this.activeImageIndex = 1;
      } else if (index === 2 && this.mockupResult?.imageUrl2) {
        this.activeImageIndex = 2;
      }
    },

    nextImage() {
      if (!this.mockupResult?.imageUrl2) return;
      this.setActiveImage(this.activeImageIndex === 1 ? 2 : 1);
    },

    prevImage() {
      if (!this.mockupResult?.imageUrl2) return;
      this.setActiveImage(this.activeImageIndex === 1 ? 2 : 1);
    },

    handleSwipe() {
      if (!this.mockupResult?.imageUrl2) return;
      const minSwipeDistance = 40; // מינימום מרחק להחלקה כדי למנוע החלפות בטעות
      const swipeDistance = this.touchEndX - this.touchStartX;

      if (swipeDistance > minSwipeDistance) {
        // החלקה ימינה - תמונה קודמת
        this.prevImage();
      } else if (swipeDistance < -minSwipeDistance) {
        // החלקה שמאלה - תמונה הבאה
        this.nextImage();
      }
    },

    // --- Form 1: Lead Capture ---
    async submitForm() {
      // 1. Validation
      this.formData.customerEmail = (this.formData.customerEmail || '').trim();
      if (!this.formData.customerEmail) {
        alert('Please enter your email address.');
        return;
      }
      
      // Double check data before submitting
      if (!this.formData.jewelrySKU || !this.formData.stoneSKU) {
         await this.loadDataFromConfirmation(); 
         if (!this.formData.jewelrySKU || !this.formData.stoneSKU) {
            alert('Product details are missing. Please refresh the page.');
            return;
         }
      }

      // 2. Set Defaults
      if (!this.formData.customerName) {
        this.formData.customerName = this.formData.customerEmail.split('@')[0] || 'Guest';
      }
      try {
        localStorage.setItem('dyo_customer_email', this.formData.customerEmail);
      } catch (e) {}

      // 3. UI State Updates
      this.showShimmer = true;
      this.loading = true;
      this.generating = true;
      this.activeImageIndex = 1;
      this.startMessageRotation();

      // 4. Generate Image
      try {
        let result;
        if (this._backgroundResult) {
          result = this._backgroundResult;
          this._backgroundResult = null;
        } else if (this._backgroundPromise) {
          result = await this._backgroundPromise;
        } else {
          result = await this.callGenerateAPI();
        }
        if (result) {
          this.mockupResult = result;
          this.activeImageIndex = 1;
          this.imageLoaded = false;
          this.success = true;
          this.updateUrlWithAiImage(result.imageUrl, result.imageUrl2);
          this.updateMockupEmail(result);
          this.sendToHubSpotLead();
        }
      } catch (error) {
        console.error('Error:', error);
        this.showShimmer = false;
        this.stopMessageRotation(true);
        alert('Failed to generate preview. Please try again.');
      } finally {
        this.loading = false;
        this.generating = false;
        this._backgroundPromise = null;
      }
    },

    sendToHubSpotLead() {
      const portalId = '49511833';
      const formId = 'c24f4aea-8498-4684-abef-fa3991fa362e';
      const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

      let metalName = '18K White Gold';
      if (this.formData.metalColor === 'yellow') metalName = '18K Yellow Gold';
      if (this.formData.metalColor === 'rose') metalName = '18K Rose Gold';

      const fields = [
        { name: 'email', value: this.formData.customerEmail },
        { name: 'stone_product_handle', value: this.formData.stoneSKU },
        { name: 'setting_product_handle', value: this.formData.jewelrySKU },
        { name: 'metal_color', value: metalName },
        { name: 'page_url', value: window.location.href }
      ];

      const context = {
        pageUri: window.location.href,
        pageName: document.title
      };

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, context })
      }).catch(err => console.warn('HubSpot tracking error:', err));
    },

    // --- Try Again Logic ---
    tryAgain() {
      this.success = false;
      this.mockupResult = null;
      this.activeImageIndex = 1;
      this.showShimmer = false;
      this.imageLoaded = false;
      this.feedback = { rating: null, submitted: false };
      this._backgroundResult = null;
      this._backgroundPromise = null;
      this.submitForm();
    },

    // --- Form 2: Contact Expert ---
    openContactForm() {
      this.showContactForm = true;
      this.$nextTick(() => {
        this.initHubSpotContactForm();
      });
    },

    closeContactForm() {
      this.showContactForm = false;
    },
    
    async submitFeedback() {
      if (!this.feedback.rating) return;
      try {
        const payload = {
          mockupId: this.mockupResult?.galleryId || null,
          imageUrl: this.mockupResult?.imageUrl || '',
          jewelrySKU: this.formData.jewelrySKU,
          stoneSKU: this.formData.stoneSKU,
          rating: this.feedback.rating,
          comment: this.feedback.comment,
          customerEmail: this.formData.customerEmail,
          pageUrl: window.location.href
        };
        await fetch('https://shopify-hubspot-sync.onrender.com/api/jewelry/mockup-feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        this.feedback.submitted = true;
      } catch (error) {
        this.feedback.submitted = true;
      }
    },

    initHubSpotContactForm() {
      const wrapper = document.getElementById('hubspot-form-wrapper');
      if (!wrapper || !window.LeibishHubSpot) return;
      // Unmount before clearing: a queued mount that is discarded without unmounting
      // would hand its prefilled fields to the next frame of the same form.
      window.LeibishHubSpot.unmount(wrapper);
      wrapper.innerHTML = '';

      window.LeibishHubSpot.mount(wrapper, {
        formId: 'd1142b86-e07d-4e20-8070-5e38d33ea0c1',
        fields: {
          email: this.formData.customerEmail,
          stone_product_handle: this.formData.stoneSKU,
          setting_product_handle: this.formData.jewelrySKU,
          page_url: window.location.href
        },
        onSubmit: () => {
          this.contactFormSuccess = true;
          setTimeout(() => { this.showContactForm = false; }, 3000);
        }
      });
    },

    startBackgroundGeneration() {
      if (!this.formData.jewelrySKU || !this.formData.stoneSKU) return;
      this._backgroundPromise = this.callGenerateAPI();
      this._backgroundPromise.then(result => {
        this._backgroundResult = result;
      }).catch(err => {
        this._backgroundResult = null;
      });
    },

    startMessageRotation() {
      this.stopMessageRotation();
      this.currentMessageIndex = 0;
      this.displayedLoadingMessage = '';
      const animationId = ++this.loadingMessageAnimationId;

      const scheduleStep = (callback, delay) => {
        this.loadingMessageTimeout = window.setTimeout(() => {
          this.loadingMessageTimeout = null;
          callback();
        }, delay);
      };

      const typeMessage = () => {
        if (animationId !== this.loadingMessageAnimationId || !this.showShimmer) return;
        const nextMessage = this.loadingMessages[this.currentMessageIndex];

        if (this.displayedLoadingMessage.length < nextMessage.length) {
          this.displayedLoadingMessage = nextMessage.slice(0, this.displayedLoadingMessage.length + 1);
          scheduleStep(typeMessage, 90);
          return;
        }
        scheduleStep(deleteMessage, 1500);
      };

      const deleteMessage = () => {
        if (animationId !== this.loadingMessageAnimationId || !this.showShimmer) return;
        if (this.displayedLoadingMessage.length > 0) {
          this.displayedLoadingMessage = this.displayedLoadingMessage.slice(0, -1);
          scheduleStep(deleteMessage, 55);
          return;
        }
        this.currentMessageIndex = (this.currentMessageIndex + 1) % this.loadingMessages.length;
        scheduleStep(typeMessage, 325);
      };
      typeMessage();
    },

    stopMessageRotation(clearMessage = false) {
      this.loadingMessageAnimationId += 1;
      if (this.loadingMessageTimeout) {
        clearTimeout(this.loadingMessageTimeout);
        this.loadingMessageTimeout = null;
      }
      if (clearMessage) this.displayedLoadingMessage = '';
    },

    onImageLoaded() {
      this.imageLoaded = true;
      this.showShimmer = false;
      this.stopMessageRotation();
    },

    async callGenerateAPI() {
      let completeUrl = window.location.href;
      if (Alpine.store('xConfirmationDYO')?.page_url) completeUrl = Alpine.store('xConfirmationDYO').page_url;

      const payload = {
        customerName: this.formData.customerName || 'Guest',
        customerEmail: this.formData.customerEmail,
        jewelrySKU: this.formData.jewelrySKU,
        stoneSKU: this.formData.stoneSKU,
        metalColor: this.formData.metalColor,
        sourceUrl: completeUrl
      };

      const response = await fetch('https://shopify-hubspot-sync.onrender.com/api/jewelry/generate-from-sku', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('API Error');
      return await response.json();
    },

    handleCallForPriceHover(el) {
       const store = Alpine.store('xConfirmationDYO');
       const formStore = Alpine.store('xFormSF');
       if (store && formStore && store.call_for_price_item_url) {
           formStore.load(store.call_for_price_item_url.split('?')[0], el, store.call_for_price_item_id, 'form-c4p');
       }
    },

    handleReserveStoneHover(el) {
        const store = Alpine.store('xConfirmationDYO');
        const formStore = Alpine.store('xFormSF');
        if (store && formStore && store.products?.stone?.urlProduct) {
            formStore.load(store.products.stone.urlProduct.split('?')[0], el, 'id-2', 'form-reserve');
        }
    },

    loadDataFromConfirmation() {
      const storedEmail = localStorage.getItem('dyo_customer_email');
      if (storedEmail) {
        this.formData.customerEmail = storedEmail;
        this.formData.customerName = storedEmail.split('@')[0];
      }

      const urlParams = new URLSearchParams(window.location.search);
      const aiImage = urlParams.get('ai_image');
      const aiImage2 = urlParams.get('ai_image2');
      
      if (aiImage) {
        this.mockupResult = { 
            imageUrl: decodeURIComponent(aiImage),
            imageUrl2: aiImage2 ? decodeURIComponent(aiImage2) : null
        };
        this.activeImageIndex = 1;
        this.success = true;
        this.showShimmer = true;
        this.imageLoaded = false;
        this.startMessageRotation();
      }

      const store = Alpine.store('xConfirmationDYO');
      if (store) {
          if (store.products?.setting?.productVariantSku) this.formData.jewelrySKU = store.products.setting.productVariantSku;
          if (store.products?.stone?.productVariantSku) this.formData.stoneSKU = store.products.stone.productVariantSku;
      }
      
      if (!this.formData.jewelrySKU || !this.formData.stoneSKU) {
          try {
              const dyoDataString = localStorage.getItem('dyo_data');
              if (dyoDataString) {
                const dyoData = JSON.parse(dyoDataString);
                if (!this.formData.jewelrySKU && dyoData.process?.setting?.variants) {
                  const v = dyoData.process.setting.variants.find(v => v.id == dyoData.process.setting.productVariantId);
                  if (v?.sku) {
                    this.formData.jewelrySKU = v.sku;
                    const metal = this.extractMetalColorFromText(v.title || v.public_title);
                    if (metal) this.formData.metalColor = metal;
                  }
                }
                if (!this.formData.stoneSKU && dyoData.process?.stone?.productVariantId) {
                  if (dyoData.process?.stone?.variants) {
                    const v = dyoData.process.stone.variants.find(v => v.id == dyoData.process.stone.productVariantId);
                    if (v?.sku) this.formData.stoneSKU = v.sku;
                    else this.formData.stoneSKU = dyoData.process.stone.productVariantId;
                  } else {
                    this.formData.stoneSKU = dyoData.process.stone.productVariantId;
                  }
                }
              }
          } catch(e) {}
      }

      if (this.formData.metalColor === 'original') {
        const variantElement = document.getElementById('variant-1');
        if (variantElement) {
          const metal = this.extractMetalColorFromText(variantElement.textContent);
          if (metal) this.formData.metalColor = metal;
        }
      }

      if (this.formData.jewelrySKU && this.formData.stoneSKU && !this.success && !aiImage) {
          this.startBackgroundGeneration();
      }
    },
    
    extractMetalColorFromText(text) {
      if (!text) return null;
      const lowerText = text.toLowerCase();
      if (lowerText.includes('white')) return 'white';
      if (lowerText.includes('yellow')) return 'yellow';
      if (lowerText.includes('rose')) return 'rose';
      if (lowerText.includes('platinum')) return 'white';
      return null;
    },
    
    updateUrlWithAiImage(imageUrl, imageUrl2) {
        if (!imageUrl) return;
        const store = Alpine.store('xConfirmationDYO');
        const baseUrl = store?.page_url || window.location.href;
        let url = new URL(baseUrl);
        url.searchParams.set('ai_image', imageUrl);
        if (imageUrl2) {
            url.searchParams.set('ai_image2', imageUrl2);
        }
        url.hash = 'dyo';
        const fullUrl = url.toString();
        window.history.replaceState({}, '', fullUrl);
        if (store) store.page_url = fullUrl;
    },
    
    copyMockupLink() {
        const url = window.location.href;
        const onSuccess = () => {
            this.linkCopied = true;
            setTimeout(() => { this.linkCopied = false; }, 2000);
        };
        navigator.clipboard.writeText(url).then(onSuccess).catch(() => {
            const input = document.createElement('input');
            input.value = url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            onSuccess();
        });
    },

    shareWhatsApp() {
        const url = encodeURIComponent(window.location.href);
        window.open('https://wa.me/?text=' + url, '_blank');
    },

    updateMockupEmail(result) {
        fetch('https://shopify-hubspot-sync.onrender.com/api/jewelry/update-mockup-email', {
             method: 'POST',
             headers: {'Content-Type': 'application/json'},
             body: JSON.stringify({
                 imageUrl: result.imageUrl,
                 customerEmail: this.formData.customerEmail,
                 mockupId: result.galleryId
             })
        }).catch(e => console.log(e));
    }
  };
}
